import { useCallback, useEffect, useState } from 'react'
import { FiPlay } from 'react-icons/fi'
import { getVideoUrlValue, isExternalVideoUrl, isUploadedVideo, resolveExternalVideo } from '../../utils/videoUrl'

export default function VideoUrlFrame({ video, className = '' }) {
  const [playing, setPlaying] = useState(false)
  const [failed, setFailed] = useState(false)
  const [failureMessage, setFailureMessage] = useState('')
  const [fallbackTabBlocked, setFallbackTabBlocked] = useState(false)
  const externalUrl = getVideoUrlValue(video)
  const isExternal = isExternalVideoUrl(video)
  const isUploaded = isUploadedVideo(video)
  const resolvedVideo = externalUrl ? resolveExternalVideo(video) : null
  const thumbnail = video?.thumbnail || video?.poster || resolvedVideo?.thumbnail || ''
  const providerEmbedUrl = resolvedVideo?.provider === 'youtube'
    ? `${resolvedVideo.playbackUrl}&enablejsapi=1${typeof window !== 'undefined' ? `&origin=${encodeURIComponent(window.location.origin)}` : ''}`
    : resolvedVideo?.provider === 'vimeo'
      ? `${resolvedVideo.playbackUrl}${resolvedVideo.playbackUrl.includes('?') ? '&' : '?'}api=1`
      : resolvedVideo?.playbackUrl

  const openOriginalUrl = useCallback(() => {
    if (!externalUrl || typeof window === 'undefined') return false
    const directWindow = window.open(externalUrl, '_blank', 'noopener,noreferrer')
    if (directWindow) {
      directWindow.focus?.()
      return true
    }

    const fallbackLink = document.createElement('a')
    fallbackLink.href = externalUrl
    fallbackLink.target = '_blank'
    fallbackLink.rel = 'noopener noreferrer'
    fallbackLink.click()
    return false
  }, [externalUrl])

  const failPlayback = useCallback(() => {
    if (failed || !externalUrl) return
    setFailed(true)
    setPlaying(false)
    setFailureMessage('Video playback failed. Opening the original video in a new tab...')
    setFallbackTabBlocked(!openOriginalUrl())
  }, [externalUrl, failed, openOriginalUrl])

  useEffect(() => {
    if (!playing || resolvedVideo?.playbackType !== 'embed') return undefined

    const handleProviderMessage = (event) => {
      if (resolvedVideo.provider === 'youtube' && event.origin !== 'https://www.youtube-nocookie.com' && event.origin !== 'https://www.youtube.com') return
      if (resolvedVideo.provider === 'vimeo' && event.origin !== 'https://player.vimeo.com') return

      let message = event.data
      try {
        if (typeof message === 'string') message = JSON.parse(message)
      } catch {
        return
      }

      const youtubeErrorCode = Number(message?.info ?? message?.error?.code ?? message?.error)
      const youtubeError = resolvedVideo.provider === 'youtube' && (
        message?.event === 'onError' ||
        message?.event === 'error' ||
        [101, 150].includes(youtubeErrorCode)
      )
      const vimeoError = resolvedVideo.provider === 'vimeo' && (
        message?.event === 'error' ||
        message?.method === 'error' ||
        Boolean(message?.error)
      )
      if (youtubeError || vimeoError) failPlayback()
    }

    window.addEventListener('message', handleProviderMessage)
    return () => window.removeEventListener('message', handleProviderMessage)
  }, [playing, resolvedVideo?.playbackType, resolvedVideo?.provider, failPlayback])

  if (failed && failureMessage) {
    return (
      <div className={`flex aspect-video w-full items-center justify-center rounded-lg bg-gray-900 px-4 text-center text-sm text-white ${className}`} role="status">
        {failureMessage}
        {fallbackTabBlocked && (
          <a href={externalUrl} target="_blank" rel="noopener,noreferrer" className="ml-2 underline">
            Open video
          </a>
        )}
      </div>
    )
  }

  if (failed) return null

  if (isExternal && externalUrl) {
    const externalEmbedUrl = resolvedVideo?.playbackType === 'embed'
      ? providerEmbedUrl
      : ''

    if (playing && externalEmbedUrl) {
      return (
        <div className={`relative w-full aspect-video overflow-hidden rounded-lg bg-black ${className}`}>
          <iframe
            src={`${externalEmbedUrl}${externalEmbedUrl.includes('?') ? '&' : '?'}autoplay=1`}
            title="External video player"
            className="h-full w-full border-0"
            onClick={(event) => event.stopPropagation()}
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
            allowFullScreen
            onLoad={(event) => {
              const message = resolvedVideo.provider === 'youtube'
                ? { event: 'command', func: 'addEventListener', args: ['onError'] }
                : { method: 'addEventListener', value: 'error' }
              event.currentTarget.contentWindow?.postMessage(JSON.stringify(message), '*')
            }}
            onError={failPlayback}
          />
        </div>
      )
    }

    if (playing && resolvedVideo?.playbackType === 'html5') {
      return (
        <div className={`relative w-full aspect-video overflow-hidden rounded-lg bg-black ${className}`}>
          <video
            src={externalUrl}
            className="h-full w-full object-contain"
            controls
            autoPlay
            playsInline
            preload="metadata"
            onError={failPlayback}
          />
        </div>
      )
    }

    if (playing) {
      return (
        <div className={`flex aspect-video w-full flex-col items-center justify-center gap-2 rounded-lg bg-gray-900 px-4 text-center text-white ${className}`}>
          <FiPlay className="h-8 w-8 text-white/80" />
          <p className="text-sm font-semibold">Embedded Playback Unavailable</p>
          <p className="text-xs text-white/70">This video provider does not allow this video to be played directly inside this website.</p>
        </div>
      )
    }

    return (
      <button
        type="button"
        onClick={(event) => {
          event.preventDefault()
          event.stopPropagation()
          if (resolvedVideo?.playbackType === 'none') {
            setFailed(true)
            setFailureMessage('This video cannot be embedded. Opening the original video in a new tab...')
            setFallbackTabBlocked(!openOriginalUrl())
            return
          }
          setPlaying(true)
        }}
        className={`group relative block w-full aspect-video overflow-hidden rounded-lg bg-gray-900 ${className}`}
        aria-label="Play video"
      >
        {thumbnail ? (
          <img src={thumbnail} alt="Video preview" className="w-full h-full object-cover" />
        ) : resolvedVideo?.playbackType === 'html5' ? (
          <video
            src={externalUrl}
            className="w-full h-full object-cover pointer-events-none"
            muted
            playsInline
            preload="metadata"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-gray-800 via-gray-900 to-black flex items-center justify-center">
            <span className="text-xs font-medium uppercase tracking-[0.2em] text-white/70">
              {resolvedVideo?.provider || 'Video'}
            </span>
          </div>
        )}
        <span className="absolute inset-0 flex items-center justify-center bg-black/20 transition group-hover:bg-black/30">
          <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-black/70 text-white shadow-lg">
            <FiPlay className="w-7 h-7 ml-1" />
          </span>
        </span>
      </button>
    )
  }

  if (video?.embedUrl && !isExternal && !isUploaded && playing) {
    return (
      <iframe
        src={`${video.embedUrl}${video.embedUrl.includes('?') ? '&' : '?'}autoplay=1`}
        title="Video"
        className={`w-full aspect-video border-0 ${className}`}
        onClick={(event) => event.stopPropagation()}
        allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
        allowFullScreen
      />
    )
  }

  if (video?.embedUrl && !isExternal && !isUploaded) {
    return (
      <button type="button" onClick={(event) => { event.stopPropagation(); setPlaying(true) }} className={`relative block w-full aspect-video overflow-hidden bg-gray-900 ${className}`} aria-label="Play video">
        {thumbnail ? <img src={thumbnail} alt="Video preview" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gray-800" />}
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-black/70 text-white shadow-lg">
            <FiPlay className="w-7 h-7 ml-1" />
          </span>
        </span>
      </button>
    )
  }

  return (
    <video src={externalUrl || video?.url || ''} controls playsInline preload="metadata" poster={thumbnail || undefined} className={`w-full h-auto ${className}`}>
      Your browser does not support video playback.
    </video>
  )
}




// import { useEffect, useState } from 'react'
// import { FiPlay } from 'react-icons/fi'
// import { getVideoUrlValue, isExternalVideoUrl, isUploadedVideo, resolveExternalVideo } from '../../utils/videoUrl'

// export default function VideoUrlFrame({ video, className = '' }) {
//   const [playing, setPlaying] = useState(false)
//   const [failed, setFailed] = useState(false)
//   const externalUrl = getVideoUrlValue(video)
//   const isExternal = isExternalVideoUrl(video)
//   const isUploaded = isUploadedVideo(video)
//   const resolvedVideo = externalUrl ? resolveExternalVideo(video) : null
//   const thumbnail = video?.thumbnail || video?.poster || resolvedVideo?.thumbnail || ''
//   const providerEmbedUrl = resolvedVideo?.provider === 'youtube'
//     ? `${resolvedVideo.playbackUrl}&enablejsapi=1${typeof window !== 'undefined' ? `&origin=${encodeURIComponent(window.location.origin)}` : ''}`
//     : resolvedVideo?.provider === 'vimeo'
//       ? `${resolvedVideo.playbackUrl}${resolvedVideo.playbackUrl.includes('?') ? '&' : '?'}api=1`
//       : resolvedVideo?.playbackUrl
//   const failPlayback = () => {
//     if (failed || !externalUrl) return
//     setFailed(true)
//     setPlaying(false)
//     window.open(externalUrl, '_blank', 'noopener,noreferrer')
//   }

//   useEffect(() => {
//     if (!playing || resolvedVideo?.playbackType !== 'embed') return undefined

//     const handleProviderMessage = (event) => {
//       if (resolvedVideo.provider === 'youtube' && event.origin !== 'https://www.youtube-nocookie.com' && event.origin !== 'https://www.youtube.com') return
//       if (resolvedVideo.provider === 'vimeo' && event.origin !== 'https://player.vimeo.com') return

//       let message = event.data
//       try {
//         if (typeof message === 'string') message = JSON.parse(message)
//       } catch {
//         return
//       }

//       const youtubeError = resolvedVideo.provider === 'youtube' && message?.event === 'onError'
//       const vimeoError = resolvedVideo.provider === 'vimeo' && (message?.event === 'error' || message?.method === 'error')
//       if (youtubeError || vimeoError) failPlayback()
//     }

//     window.addEventListener('message', handleProviderMessage)
//     return () => window.removeEventListener('message', handleProviderMessage)
//   }, [playing, resolvedVideo?.playbackType, resolvedVideo?.provider, externalUrl, failed])

//   if (failed) return null

//   if (isExternal && externalUrl) {
//     const externalEmbedUrl = resolvedVideo?.playbackType === 'embed'
//       ? providerEmbedUrl
//       : ''

//     if (playing && externalEmbedUrl) {
//       return (
//         <div className={`relative w-full aspect-video overflow-hidden rounded-lg bg-black ${className}`}>
//           <iframe
//             src={`${externalEmbedUrl}${externalEmbedUrl.includes('?') ? '&' : '?'}autoplay=1`}
//             title="External video player"
//             className="h-full w-full border-0"
//             onClick={(event) => event.stopPropagation()}
//             allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
//             allowFullScreen
//             onError={failPlayback}
//           />
//         </div>
//       )
//     }

//     if (playing && resolvedVideo?.playbackType === 'html5') {
//       return (
//         <div className={`relative w-full aspect-video overflow-hidden rounded-lg bg-black ${className}`}>
//           <video
//             src={externalUrl}
//             className="h-full w-full object-contain"
//             controls
//             autoPlay
//             playsInline
//             preload="metadata"
//             onError={failPlayback}
//           />
//         </div>
//       )
//     }

//     if (playing) {
//       return (
//         <div className={`flex aspect-video w-full flex-col items-center justify-center gap-2 rounded-lg bg-gray-900 px-4 text-center text-white ${className}`}>
//           <FiPlay className="h-8 w-8 text-white/80" />
//           <p className="text-sm font-semibold">Embedded Playback Unavailable</p>
//           <p className="text-xs text-white/70">This video provider does not allow this video to be played directly inside this website.</p>
//         </div>
//       )
//     }

//     return (
//       <button
//         type="button"
//         onClick={(event) => {
//           event.preventDefault()
//           event.stopPropagation()
//           setPlaying(true)
//         }}
//         className={`group relative block w-full aspect-video overflow-hidden rounded-lg bg-gray-900 ${className}`}
//         aria-label="Play video"
//       >
//         {thumbnail ? (
//           <img src={thumbnail} alt="Video preview" className="w-full h-full object-cover" />
//         ) : resolvedVideo?.playbackType === 'html5' ? (
//           <video
//             src={externalUrl}
//             className="w-full h-full object-cover pointer-events-none"
//             muted
//             playsInline
//             preload="metadata"
//           />
//         ) : (
//           <div className="w-full h-full bg-gradient-to-br from-gray-800 via-gray-900 to-black flex items-center justify-center">
//             <span className="text-xs font-medium uppercase tracking-[0.2em] text-white/70">
//               {resolvedVideo?.provider || 'Video'}
//             </span>
//           </div>
//         )}
//         <span className="absolute inset-0 flex items-center justify-center bg-black/20 transition group-hover:bg-black/30">
//           <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-black/70 text-white shadow-lg">
//             <FiPlay className="w-7 h-7 ml-1" />
//           </span>
//         </span>
//       </button>
//     )
//   }

//   if (video?.embedUrl && !isExternal && !isUploaded && playing) {
//     return (
//       <iframe
//         src={`${video.embedUrl}${video.embedUrl.includes('?') ? '&' : '?'}autoplay=1`}
//         title="Video"
//         className={`w-full aspect-video border-0 ${className}`}
//         onClick={(event) => event.stopPropagation()}
//         allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
//         allowFullScreen
//       />
//     )
//   }

//   if (video?.embedUrl && !isExternal && !isUploaded) {
//     return (
//       <button type="button" onClick={(event) => { event.stopPropagation(); setPlaying(true) }} className={`relative block w-full aspect-video overflow-hidden bg-gray-900 ${className}`} aria-label="Play video">
//         {thumbnail ? <img src={thumbnail} alt="Video preview" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gray-800" />}
//         <span className="absolute inset-0 flex items-center justify-center">
//           <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-black/70 text-white shadow-lg">
//             <FiPlay className="w-7 h-7 ml-1" />
//           </span>
//         </span>
//       </button>
//     )
//   }

//   return (
//     <video src={externalUrl || video?.url || ''} controls playsInline preload="metadata" poster={thumbnail || undefined} className={`w-full h-auto ${className}`}>
//       Your browser does not support video playback.
//     </video>
//   )
// }
